import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Star, Check, Phone, Mail, MapPin, ChevronDown, Calendar, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, pricingRes, programsRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/pricing`),
          axios.get(`${API}/programs`)
        ]);
        setTestimonials(testimonialsRes.data);
        setPricing(pricingRes.data);
        setPrograms(programsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center hero-bg"
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_exercise-pathway/artifacts/qpkm0mxh_IMG_20251102_132227_965.webp"
            alt="M.Vision Fitness"
            className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-8 object-contain"
            data-testid="hero-logo"
          />
          <h1 
            className="font-oswald text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter mb-6 animate-fade-in"
            data-testid="hero-title"
          >
            TRANSFORM YOUR<br />VISION INTO REALITY
          </h1>
          <p className="font-manrope text-neutral-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 stagger-1 animate-fade-in">
            Personalized fitness programs designed to help you achieve your goals. 
            Take our assessment and discover the perfect program for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center stagger-2 animate-fade-in">
            <Link to="/assessment" data-testid="cta-assessment">
              <button className="btn-primary flex items-center justify-center gap-2">
                START YOUR ASSESSMENT
                <ArrowRight size={20} />
              </button>
            </Link>
            <a href="#programs" data-testid="cta-programs">
              <button className="btn-secondary">
                VIEW PROGRAMS
              </button>
            </a>
          </div>
        </div>
        <a 
          href="#about" 
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-neutral-400 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown size={32} />
        </a>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 section-divider" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-6">
                ABOUT M.VISION FITNESS
              </h2>
              <div className="w-16 h-0.5 bg-mvision-gold mb-8"></div>
              <p className="font-manrope text-neutral-400 text-lg mb-6">
                M.Vision Fitness is more than just a training program—it's a commitment to excellence. 
                As a <span className="text-mvision-gold font-semibold">NASM Certified Personal Trainer</span>, I bring 
                science-backed methodologies and personalized coaching to help clients of all fitness levels achieve their goals.
              </p>
              <p className="font-manrope text-neutral-400 text-lg mb-6">
                Our approach combines strength training, conditioning, and nutrition guidance to create 
                comprehensive programs tailored to your unique needs and objectives.
              </p>
              <p className="font-manrope text-neutral-400 text-lg mb-8">
                For online coaching clients, we use <span className="text-mvision-gold font-semibold">Everfit</span> as our 
                primary training platform—giving you access to your custom workouts, progress tracking, nutrition plans, 
                and direct messaging with your coach all in one app.
              </p>
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <p className="font-oswald text-3xl md:text-4xl font-bold text-mvision-gold">500+</p>
                  <p className="font-manrope text-neutral-500 text-sm uppercase tracking-wider">Clients Transformed</p>
                </div>
                <div>
                  <p className="font-oswald text-3xl md:text-4xl font-bold text-mvision-gold">10+</p>
                  <p className="font-manrope text-neutral-500 text-sm uppercase tracking-wider">Years Experience</p>
                </div>
                <div>
                  <p className="font-oswald text-3xl md:text-4xl font-bold text-mvision-gold">6</p>
                  <p className="font-manrope text-neutral-500 text-sm uppercase tracking-wider">Programs</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_exercise-pathway/artifacts/ckmy35oo_me.png"
                alt="M.Vision Fitness Personal Trainer"
                className="w-full h-[500px] object-cover object-top"
                data-testid="about-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-mvision-gold text-black p-6">
                <p className="font-oswald text-xl uppercase tracking-tighter">NASM Certified Personal Trainer</p>
              </div>
              {/* NASM Badge */}
              <div className="absolute -bottom-6 -right-6 bg-white p-3">
                <img 
                  src="https://www.nasm.org/images/nasmlogo.svg" 
                  alt="NASM Certified"
                  className="h-12 w-auto"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-24 md:py-32 bg-neutral-950 section-divider" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              OUR PROGRAMS
            </h2>
            <div className="w-16 h-0.5 bg-mvision-gold mx-auto mb-6"></div>
            <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto">
              Discover the program that matches your fitness goals. Each program is designed 
              to deliver results through proven training methodologies.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((program, index) => (
              <Link 
                key={program.id}
                to={`/results/${program.id}`}
                className="card-hover bg-black p-0 overflow-hidden group block"
                data-testid={`program-card-${program.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={program.image}
                    alt={program.name}
                    className="w-full h-full object-cover img-grayscale group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40"></div>
                  <p className="absolute top-4 left-4 font-oswald text-xs uppercase tracking-wider bg-white text-black px-3 py-1">
                    {program.duration}
                  </p>
                </div>
                <div className="p-6">
                  <h3 className="font-oswald text-xl font-bold uppercase tracking-tighter mb-2">
                    {program.name}
                  </h3>
                  <p className="font-manrope text-neutral-500 text-sm mb-4 line-clamp-2">
                    {program.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {program.features.slice(0, 2).map((feature, i) => (
                      <span key={i} className="text-xs text-neutral-400 border border-neutral-700 px-2 py-1">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/assessment" data-testid="programs-cta">
              <button className="btn-primary">
                FIND YOUR PERFECT PROGRAM
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 md:py-32 section-divider" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              REAL RESULTS
            </h2>
            <div className="w-16 h-0.5 bg-mvision-gold mx-auto mb-6"></div>
            <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto">
              Don't just take our word for it. See what our clients have achieved.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className="testimonial-card"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#D4AF37" stroke="#D4AF37" />
                  ))}
                </div>
                <p className="font-manrope text-lg text-neutral-300 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-oswald text-lg uppercase tracking-tighter">{testimonial.name}</p>
                    <p className="font-manrope text-neutral-500 text-sm">{testimonial.program}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-oswald text-white uppercase tracking-tighter">{testimonial.result}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32 bg-neutral-950 section-divider" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              INVESTMENT IN YOURSELF
            </h2>
            <div className="w-16 h-0.5 bg-mvision-gold mx-auto mb-6"></div>
            <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto">
              Choose the package that fits your commitment level. All packages include 
              access to your personalized program.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((pkg, index) => (
              <PricingCard key={pkg.id} pkg={pkg} />
            ))}
          </div>

          {/* Book Free Consultation CTA */}
          <div className="text-center mt-12 pt-12 border-t border-neutral-800">
            <p className="font-manrope text-neutral-400 mb-4">Not ready to commit? Start with a free consultation.</p>
            <Link to="/booking" data-testid="book-consultation-cta">
              <button className="btn-secondary flex items-center gap-2 mx-auto">
                <Calendar size={18} />
                BOOK FREE CONSULTATION
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 section-divider" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
                GET IN TOUCH
              </h2>
              <div className="w-16 h-0.5 bg-mvision-gold mb-8"></div>
              <p className="font-manrope text-neutral-400 text-lg mb-8">
                Ready to start your transformation? Have questions about our programs? 
                Reach out and let's discuss how we can help you achieve your goals.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-neutral-700 flex items-center justify-center">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="font-oswald uppercase tracking-wider text-sm text-neutral-500">Phone</p>
                    <a href="tel:702-815-5171" className="font-manrope hover:text-neutral-400 transition-colors">(702) 815-5171</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-neutral-700 flex items-center justify-center">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="font-oswald uppercase tracking-wider text-sm text-neutral-500">Email</p>
                    <a href="mailto:mitchv51@yahoo.com" className="font-manrope hover:text-neutral-400 transition-colors">mitchv51@yahoo.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-neutral-700 flex items-center justify-center">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-oswald uppercase tracking-wider text-sm text-neutral-500">Location</p>
                    <p className="font-manrope">Las Vegas, NV - Online & In-Person</p>
                  </div>
                </div>
              </div>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    preferred_package: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "", preferred_package: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="bg-neutral-950 border border-neutral-800 p-8 flex flex-col items-center justify-center text-center">
        <Check size={48} className="text-white mb-4" />
        <h3 className="font-oswald text-2xl uppercase tracking-tighter mb-2">MESSAGE SENT</h3>
        <p className="font-manrope text-neutral-400">We'll get back to you within 24 hours.</p>
        <button 
          onClick={() => setSubmitted(false)}
          className="btn-secondary mt-6"
          data-testid="send-another-btn"
        >
          SEND ANOTHER MESSAGE
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div>
        <input
          type="text"
          placeholder="Your Name"
          className="input-field"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="contact-name-input"
        />
      </div>
      <div>
        <input
          type="email"
          placeholder="Your Email"
          className="input-field"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          data-testid="contact-email-input"
        />
      </div>
      <div>
        <input
          type="tel"
          placeholder="Phone Number (Optional)"
          className="input-field"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          data-testid="contact-phone-input"
        />
      </div>
      <div>
        <select
          className="input-field cursor-pointer"
          value={formData.preferred_package}
          onChange={(e) => setFormData({ ...formData, preferred_package: e.target.value })}
          data-testid="contact-package-select"
        >
          <option value="">Select Package (Optional)</option>
          <option value="starter">Starter - $99/month</option>
          <option value="pro">Pro - $199/month</option>
          <option value="elite">Elite - $349/month</option>
        </select>
      </div>
      <div>
        <textarea
          placeholder="Your Message"
          className="input-field min-h-[120px] resize-none"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          required
          data-testid="contact-message-input"
        ></textarea>
      </div>
      <button 
        type="submit" 
        className="btn-primary w-full" 
        disabled={loading}
        data-testid="contact-submit-btn"
      >
        {loading ? "SENDING..." : "SEND MESSAGE"}
      </button>
    </form>
  );
};

const PricingCard = ({ pkg }) => {
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        package_id: pkg.id,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div 
      className={`pricing-card ${pkg.popular ? 'popular' : ''}`}
      data-testid={`pricing-${pkg.id}`}
    >
      {pkg.popular && (
        <p className="font-oswald text-xs uppercase tracking-wider text-black bg-white px-3 py-1 inline-block mb-4">
          MOST POPULAR
        </p>
      )}
      <h3 className="font-oswald text-2xl font-bold uppercase tracking-tighter mb-2">
        {pkg.name}
      </h3>
      <div className="flex items-baseline gap-1 mb-6">
        <span className="font-oswald text-5xl font-bold text-mvision-gold">${pkg.price}</span>
        <span className="font-manrope text-neutral-500">/{pkg.duration}</span>
      </div>
      <ul className="space-y-3 mb-8">
        {pkg.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check size={18} className="text-white mt-1 flex-shrink-0" />
            <span className="font-manrope text-neutral-400">{feature}</span>
          </li>
        ))}
      </ul>
      <button 
        onClick={handlePayment}
        disabled={loading}
        className={`${pkg.popular ? 'btn-primary' : 'btn-secondary'} w-full flex items-center justify-center gap-2`}
        data-testid={`pricing-cta-${pkg.id}`}
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            PROCESSING...
          </>
        ) : (
          "GET STARTED"
        )}
      </button>
    </div>
  );
};

export default HomePage;
